/**
 * "Is this an RFQ?" — what the classifier decided, and the user's power to overrule it.
 *
 * ---- Why a correction control exists ----
 *
 * `classifyRfqMail` is a weighted keyword table, not a model (`mailClassify.ts`'s header says so at
 * length) and not a calibrated probability. It gets things wrong, and one real case is what
 * prompted this: a supplier's quotation, subject `RFQ-2026-00013"`, badged "RFQ? 55%" with a button
 * offering to create an enquiry from it. A heuristic nobody can correct teaches people to ignore
 * the badge everywhere, including where it is right.
 *
 * Reply DETECTION is separately improved (`isSupplierReply` now recognises our own bare document
 * numbers, which is what that mail carried), so this control is the backstop for what detection
 * still misses — not the primary fix.
 *
 * ---- Honest about where the correction goes ----
 *
 * It is held in memory for the session. `Communication` writes are refused for the app's role —
 * measured, all three routes, see `lib/mailVerdictOverride.ts` — so it cannot yet be persisted, and
 * the bar SAYS so instead of implying it stuck. Filed with the Frappe team.
 */

import { Badge } from '../../components/Badge'
import { Button } from '../../components/ui'
import { classifyRfqMail, confidencePercent, explainClassification } from '../../lib/mailClassify'
import { readsAsSupplierReply, setOverride } from '../../lib/mailVerdictOverride'
import { useVerdictOverride } from './useVerdictOverride'
import type { MailMessage } from '../../lib/types'

export function ClassificationBar({ mail }: { mail: MailMessage }) {
  const override = useVerdictOverride(mail.id)
  const result = classifyRfqMail(mail)
  const reply = readsAsSupplierReply(mail)

  const current: 'reply' | 'rfq' | 'other' = reply ? 'reply' : override === 'rfq' || result.verdict === 'rfq' ? 'rfq' : 'other'

  return (
    <div
      style={{
        borderTop: '1px solid var(--line)',
        marginTop: 14,
        paddingTop: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Read as</span>

      {current === 'reply' ? (
        <Badge tone="violet" title={override === 'supplier-reply' ? 'You marked this.' : explainClassification(result)}>
          a supplier's reply
        </Badge>
      ) : current === 'rfq' ? (
        <Badge tone="warn" title={explainClassification(result)}>
          a customer RFQ{override === 'rfq' ? '' : ` · ${confidencePercent(result.confidence)}`}
        </Badge>
      ) : (
        <Badge tone="muted" title={explainClassification(result)}>
          not an RFQ
        </Badge>
      )}

      <span style={{ flex: 1 }} />

      {override !== null ? (
        <>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>corrected by you · this session only</span>
          <Button size="sm" onClick={() => setOverride(mail.id, null)}>
            Undo
          </Button>
        </>
      ) : (
        <>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>Wrong?</span>
          {current !== 'reply' ? (
            <Button size="sm" onClick={() => setOverride(mail.id, 'supplier-reply')}>
              It's a supplier reply
            </Button>
          ) : null}
          {current !== 'rfq' ? (
            <Button size="sm" onClick={() => setOverride(mail.id, 'rfq')}>
              It's a customer RFQ
            </Button>
          ) : null}
          {current !== 'other' ? (
            <Button size="sm" onClick={() => setOverride(mail.id, 'not-rfq')}>
              Neither
            </Button>
          ) : null}
        </>
      )}
    </div>
  )
}
